import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Alert, Button } from "reactstrap";

import type { RootState } from "../../redux/reducers/session/state";

interface MigrationState {
  taxProfileComplete?: boolean | null;
  hasOldBillingData?: boolean | null;
  daysRemaining?: number | null;
  inGracePeriod?: boolean | null;
  isReferrer?: boolean | null;
  [key: string]: unknown;
}

interface TaxProfileMigrationBannerProps {
  migrationStatus?: {
    state?: MigrationState | null | undefined;
    data?: MigrationState | null | undefined;
    isLoading?: boolean;
    isRefreshing?: boolean;
    error?: unknown;
    refresh?: () => Promise<void>;
    mutate?: unknown;
  } | null;
}

const TaxProfileMigrationBanner = ({ migrationStatus }: TaxProfileMigrationBannerProps) => {
  const { t } = useTranslation("taxProfile", { keyPrefix: "migrationBanner" });
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.sessionState);
  const { state: status, isLoading, error } = migrationStatus || {};

  // Don't show for admin users
  if (user?.role?.name === "Admin") return null;

  // Don't show if migrationStatus is not provided or still loading
  if (!migrationStatus || error || isLoading || !status) return null;

  // Don't show if tax profile is complete
  if (status.taxProfileComplete) return null;

  // Don't show if user has no old billing data (new user)
  if (!status.hasOldBillingData) return null;

  const daysRemaining = status.daysRemaining ?? 60;
  const inGracePeriod = status.inGracePeriod ?? true;
  const isReferrer = status.isReferrer ?? false;

  // Determine alert variant based on days remaining
  const getVariant = () => {
    if (!inGracePeriod) return "danger";
    if (daysRemaining <= 7) return "danger";
    return "warning";
  };

  const getIcon = () => {
    if (!inGracePeriod || daysRemaining <= 7) return "🚨";
    if (daysRemaining <= 15) return "⚠️";
    return "🔔";
  };

  const getMessage = () => {
    if (!inGracePeriod) {
      return (
        <>
          <h4 className="alert-heading">{t("suspended.title")}</h4>
          <p>{t("suspended.description")}</p>
          {isReferrer && <p className="mb-0">{t("suspended.commissionBlocked")}</p>}
        </>
      );
    }

    return (
      <>
        <h4 className="alert-heading">
          {getIcon()} {t("warning.title")}
        </h4>
        <p>{t("warning.description")}</p>
        {daysRemaining <= 7 && (
          <p className="text-danger mb-2">
            <strong>{t("warning.urgentWarning")}</strong>
          </p>
        )}
        <p className="mb-0">
          <strong>
            {daysRemaining > 0 ? String(t("daysRemaining", { count: daysRemaining })) : String(t("deadlineReached"))}
          </strong>
        </p>
      </>
    );
  };

  return (
    <Alert
      color={getVariant()}
      isOpen={true}
      toggle={inGracePeriod && daysRemaining > 7 ? undefined : undefined}
      className="mb-3"
    >
      {getMessage()}
      <div className="mt-3">
        <Button
          color={inGracePeriod ? "primary" : "danger"}
          onClick={() => navigate("/settings/billing?openWizard=true")}
        >
          {inGracePeriod ? t("completeButton") : t("completeButtonNow")}
        </Button>
      </div>
    </Alert>
  );
};

export default TaxProfileMigrationBanner;
